import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { App as CapacitorApp } from '@capacitor/app'
import { Keyboard, KeyboardResize } from '@capacitor/keyboard'
import { SplashScreen } from '@capacitor/splash-screen'
import { StatusBar, Style } from '@capacitor/status-bar'
import { Fingerprint, LogOut } from 'lucide-react'
import { accountSignOut, isAccountLoggedIn } from '../accountAuth'
import { biometricUnlock, isBiometricUnlockEnabled, isNativeApp } from '../native'
import { Button } from './ui'
import { Logo } from './Logo'

const safePathFromUrl = (url: string) => {
  try {
    const parsed = new URL(url)
    return parsed.hostname === 'youwont.xyz' && parsed.pathname.startsWith('/')
      ? `${parsed.pathname}${parsed.search}${parsed.hash}`
      : null
  } catch {
    return null
  }
}

export const NativeShell = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [locked, setLocked] = useState(false)
  const [appActive, setAppActive] = useState(true)
  const [unlocking, setUnlocking] = useState(false)
  const [unlockError, setUnlockError] = useState<string | null>(null)

  const checkLock = useCallback(async () => {
    try {
      if (!isNativeApp() || !isAccountLoggedIn() || !(await isBiometricUnlockEnabled())) return
      setLocked(true)
    } catch {
      setLocked(false)
    }
  }, [])

  const unlock = useCallback(async () => {
    setUnlocking(true)
    setUnlockError(null)
    try {
      if (!(await biometricUnlock())) throw new Error('Biometric unlock is unavailable')
      setLocked(false)
    } catch {
      setUnlockError('Could not verify your identity. Try again or sign out.')
    } finally {
      setUnlocking(false)
    }
  }, [])

  useEffect(() => {
    if (!isNativeApp()) return
    void Keyboard.setResizeMode({ mode: KeyboardResize.Native })
    void StatusBar.setStyle({ style: Style.Dark })
    void StatusBar.setBackgroundColor({ color: '#eff6ff' })
    void SplashScreen.hide()

    const handles: Array<{ remove: () => Promise<void> }> = []
    void CapacitorApp.getLaunchUrl().then((launch) => {
      const path = launch?.url ? safePathFromUrl(launch.url) : null
      if (path && path !== `${location.pathname}${location.search}${location.hash}`) navigate(path)
    })
    void CapacitorApp.addListener('appUrlOpen', ({ url }) => {
      const path = safePathFromUrl(url)
      if (path) navigate(path)
    }).then((handle) => handles.push(handle))
    void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      setAppActive(isActive)
      if (!isActive) void checkLock()
    }).then((handle) => handles.push(handle))
    void checkLock()
    return () => { handles.forEach((handle) => void handle.remove()) }
  }, [checkLock, location.hash, location.pathname, location.search, navigate])

  useEffect(() => {
    if (locked && appActive) void unlock()
  }, [appActive, locked, unlock])

  if (!locked) return <>{children}</>

  return (
    <main className="native-lock app-bg">
      <section className="brand-panel native-lock-card">
        <Logo size="xl" />
        <div className="native-lock-icon"><Fingerprint size={34} /></div>
        <h1>Welcome back</h1>
        <p>Unlock YOWO to manage your codes.</p>
        {unlockError && <p className="text-sm text-red-600">{unlockError}</p>}
        <Button className="w-full" onClick={() => void unlock()} disabled={unlocking}>
          {unlocking ? 'Unlocking…' : 'Unlock YOWO'}
        </Button>
        <Button variant="ghost" className="w-full" onClick={() => { accountSignOut(); setLocked(false); navigate('/account/login') }}>
          <LogOut size={15} className="mr-2" />Sign out
        </Button>
      </section>
    </main>
  )
}
